// TennisTrack Hub — Pagina Profilo & Impostazioni | 029 Project
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './ProfiloUtente.css';

const ProfiloUtente = () => {
  const { utente, logout } = useAuth();
  const navigate = useNavigate();

  // Modifica dati personali
  const [formDati, setFormDati] = useState({ nome: '', cognome: '', email: '' });
  const [datiLoading, setDatiLoading] = useState(false);
  const [datiMsg, setDatiMsg] = useState('');
  const [datiErrore, setDatiErrore] = useState('');

  // Cambio password
  const [formPwd, setFormPwd] = useState({ passwordAttuale: '', nuovaPassword: '', confermaPassword: '' });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErrore, setPwdErrore] = useState('');

  // Stats
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (utente) {
      setFormDati({ nome: utente.nome || '', cognome: utente.cognome || '', email: utente.email || '' });
    }
    // Carica statistiche personali
    Promise.all([
      api.get('/atleti'),
      api.get('/template/miei')
    ]).then(([resAtleti, resTemplate]) => {
      setStats({
        atleti: resAtleti.data.atleti.length,
        template: resTemplate.data.template.length
      });
    }).catch(() => {});
  }, [utente]);

  const aggiornaDati = async (e) => {
    e.preventDefault();
    setDatiMsg('');
    setDatiErrore('');
    if (!formDati.nome.trim() || !formDati.cognome.trim()) {
      setDatiErrore('Nome e cognome sono obbligatori.');
      return;
    }
    setDatiLoading(true);
    try {
      await api.put('/auth/profilo', {
        nome: formDati.nome.trim(),
        cognome: formDati.cognome.trim()
      });
      setDatiMsg('✅ Dati aggiornati.');
    } catch (err) {
      setDatiErrore(err.response?.data?.error || 'Errore nell\'aggiornamento.');
    } finally {
      setDatiLoading(false);
    }
  };

  const cambiaPassword = async (e) => {
    e.preventDefault();
    setPwdMsg('');
    setPwdErrore('');
    if (formPwd.nuovaPassword !== formPwd.confermaPassword) {
      setPwdErrore('Le password non coincidono.');
      return;
    }
    if (formPwd.nuovaPassword.length < 6) {
      setPwdErrore('La nuova password deve essere lunga almeno 6 caratteri.');
      return;
    }
    setPwdLoading(true);
    try {
      await api.put('/auth/cambia-password', {
        passwordAttuale: formPwd.passwordAttuale,
        nuovaPassword: formPwd.nuovaPassword
      });
      setPwdMsg('✅ Password aggiornata.');
      setFormPwd({ passwordAttuale: '', nuovaPassword: '', confermaPassword: '' });
    } catch (err) {
      setPwdErrore(err.response?.data?.error || 'Errore nel cambio password.');
    } finally {
      setPwdLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Sei sicuro di voler uscire?')) {
      logout();
      navigate('/login');
    }
  };

  const pianoBadge = (piano) => ({
    demo:    <span className="t-badge t-badge-demo">Demo</span>,
    free:    <span className="t-badge t-badge-free">Free</span>,
    premium: <span className="t-badge t-badge-pro">⭐ Pro</span>,
  }[piano] || piano);

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1>👤 Il mio profilo</h1>
            <p>Gestisci i tuoi dati e le impostazioni dell'account</p>
          </div>
        </div>

        {/* Card riepilogo */}
        <div className="card profilo-header-card">
          <div className="profilo-avatar-big">
            {utente?.nome?.[0]}{utente?.cognome?.[0]}
          </div>
          <div className="profilo-info-big">
            <h2>{utente?.nome} {utente?.cognome}</h2>
            <p style={{ color: 'var(--text-secondary)', margin: '2px 0 8px' }}>{utente?.email}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              {pianoBadge(utente?.piano)}
              {utente?.piano !== 'pro' && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/premium')}
                  style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                >
                  ⭐ Upgrade Pro
                </button>
              )}
            </div>
          </div>
          {stats && (
            <div className="profilo-stats">
              <div className="profilo-stat">
                <span className="profilo-stat-num">{stats.atleti}</span>
                <span className="profilo-stat-label">Atleti</span>
              </div>
              <div className="profilo-stat">
                <span className="profilo-stat-num">{stats.template}</span>
                <span className="profilo-stat-label">Template</span>
              </div>
            </div>
          )}
        </div>

        <div className="profilo-grid">

          {/* Dati personali */}
          <div className="card">
            <h3 className="card-section-title">✏️ Dati personali</h3>
            {datiMsg && <div className="alert alert-success">{datiMsg}</div>}
            {datiErrore && <div className="alert alert-error">{datiErrore}</div>}
            <form onSubmit={aggiornaDati}>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Nome *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formDati.nome}
                    onChange={e => setFormDati(f => ({ ...f, nome: e.target.value }))}
                    maxLength={100}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cognome *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formDati.cognome}
                    onChange={e => setFormDati(f => ({ ...f, cognome: e.target.value }))}
                    maxLength={100}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formDati.email}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  L'email non può essere modificata.
                </p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={datiLoading}>
                  {datiLoading ? 'Salvataggio...' : '💾 Salva'}
                </button>
              </div>
            </form>
          </div>

          {/* Cambio password */}
          <div className="card">
            <h3 className="card-section-title">🔐 Cambia password</h3>
            {pwdMsg && <div className="alert alert-success">{pwdMsg}</div>}
            {pwdErrore && <div className="alert alert-error">{pwdErrore}</div>}
            <form onSubmit={cambiaPassword}>
              <div className="form-group">
                <label className="form-label">Password attuale *</label>
                <input
                  type="password"
                  className="form-input"
                  value={formPwd.passwordAttuale}
                  onChange={e => setFormPwd(f => ({ ...f, passwordAttuale: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nuova password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={formPwd.nuovaPassword}
                  onChange={e => setFormPwd(f => ({ ...f, nuovaPassword: e.target.value }))}
                  minLength={6}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Conferma nuova password *</label>
                <input
                  type="password"
                  className="form-input"
                  value={formPwd.confermaPassword}
                  onChange={e => setFormPwd(f => ({ ...f, confermaPassword: e.target.value }))}
                  minLength={6}
                  required
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={pwdLoading}>
                  {pwdLoading ? 'Aggiornamento...' : '🔐 Cambia password'}
                </button>
              </div>
            </form>
          </div>

        </div>

        {/* Piano corrente */}
        <div className="card" style={{ marginTop: '0' }}>
          <h3 className="card-section-title">💳 Piano corrente</h3>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                {pianoBadge(utente?.piano)}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: 0 }}>
                {utente?.piano === 'pro'
                  ? 'Hai accesso a tutte le funzionalità Pro di TennisTrack Hub.'
                  : utente?.piano === 'demo'
                    ? 'Sei in modalità demo. Registrati per accedere a tutte le funzionalità.'
                    : 'Piano gratuito. Passa a Pro per sbloccare PDF, template illimitati e molto altro.'}
              </p>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/premium')}
            >
              {utente?.piano === 'pro' ? 'Gestisci abbonamento →' : '⭐ Scopri Pro →'}
            </button>
          </div>
        </div>

        {/* Zona pericolosa */}
        <div className="card card-danger">
          <h3 className="card-section-title" style={{ color: '#ef5350' }}>⚠️ Zona account</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
            Esci dall'account su questo dispositivo.
          </p>
          <button className="btn btn-danger" onClick={handleLogout}>
            🚪 Esci dall'account
          </button>
        </div>

        {/* Footer brand */}
        <div style={{ textAlign: 'center', marginTop: '32px', marginBottom: '8px' }}>
          <img src="/img/madeby029.png" alt="029 Project" style={{ height: '28px', opacity: 0.5 }} />
        </div>

      </main>
    </div>
  );
};

export default ProfiloUtente;
